import { reactive } from 'vue';
import { toCustomerProfile } from '@/api/sirta/adapters/customerPage/customerProfileAdapter';
import { CUSTOMER_URI } from '@/api/sirta/sirtaEndpoints';
import useAppFetch from '@/api/utils/useAppFetch';
import type { CustomerDto, APIResponse, CustomerProfile } from '@/types';
import type { AsyncStateReturn, UseAsyncStateOptions } from '@/components/common/AsyncLoader/asyncLoader';
import { useAsyncState } from '@vueuse/core';

export default function useGetCustomerProfileApi(asyncStateOptions: UseAsyncStateOptions<false, APIResponse<CustomerProfile>> = {}) {
  const customerResponse: APIResponse<CustomerProfile> = reactive({
    data: null,
    problem: undefined,
  });
  const appFetch = useAppFetch<CustomerDto>(CUSTOMER_URI, { immediate: false });
  const { execute, apiResponse, onFetchError, onFetchResponse, data } =
    appFetch;

  appFetch.get().json();

  onFetchResponse(() => {
    if (data.value !== null) {
      Object.assign(customerResponse, {
        data: toCustomerProfile(data.value),
        problem: undefined,
      });
    }
  });

  onFetchError((err: Error) => {
    Object.assign(customerResponse, {
      data: null,
      problem: apiResponse.problem,
    });
    throw err;
  });

  function fetchData(): AsyncStateReturn<CustomerProfile> {
    return useAsyncState(async () => {
      await execute();
      return customerResponse;
    }, customerResponse, asyncStateOptions);
  }

  return {
    fetchData,
    apiResponse: customerResponse,
  };
}
